/** Normalize dash / space variants from Excel, Word, or Jira paste. */
function normalizeBulletSource(text) {
  return String(text)
    .replace(/\uFEFF/g, "")
    .replace(/[\u2013\u2014\u2212]/g, "-")
    .replace(/\u00a0/g, " ");
}

function parseDashLine(line) {
  const t = String(line).trim();
  if (!/^-\s/.test(t)) return null;
  const text = t.replace(/^-\s+/, "").trim();
  return text ? { type: "dash", text } : null;
}

function parseNumberedLine(line) {
  const t = String(line).trim();
  // Require whitespace after "." so decimals like 1299.99 are not list markers.
  const m = t.match(/^(\d+)\.\s+(.*)$/);
  if (!m) return null;
  const text = m[2].trim();
  return text ? { type: "numbered", number: parseInt(m[1], 10), text } : null;
}

function parseListLine(line) {
  return parseDashLine(line) || parseNumberedLine(line);
}

function parseDashItems(raw, lines) {
  if (lines.length > 1) {
    const dashLines = lines.filter((l) => /^-\s/.test(l));
    if (dashLines.length > 0) {
      const items = dashLines.map((l) => l.replace(/^-\s+/, "").trim()).filter(Boolean);
      return items.length ? items : null;
    }
  }

  if (/^-\s/.test(raw)) {
    const items = raw
      .split(/\s+-\s+/)
      .map((part, i) => {
        let s = part.trim();
        if (i === 0) s = s.replace(/^-\s+/, "");
        return s.trim();
      })
      .filter(Boolean);
    return items.length ? items : null;
  }

  return null;
}

/** "1. foo" lines or inline "foo 2.bar 3.baz" */
function parseNumberedItems(raw, lines) {
  if (lines.length > 1) {
    const numbered = lines.filter((l) => /^\d+\.\s+/.test(l));
    if (numbered.length > 0) {
      const items = numbered.map((l) => l.replace(/^\d+\.\s+/, "").trim()).filter(Boolean);
      return items.length ? items : null;
    }
  }

  if (!/\d+\.\s+/.test(raw)) return null;

  const parts = raw
    .split(/(?:^|(?<=\S))\s*(?=\d+\.\s+)/)
    .map((part) => part.replace(/^\d+\.\s+/, "").trim())
    .filter(Boolean);

  if (parts.length > 1) return parts;

  if (/^\d+\.\s+/.test(raw)) {
    const one = raw.replace(/^\d+\.\s+/, "").trim();
    return one ? [one] : null;
  }

  return null;
}

/**
 * Parse text that uses "- " or "1." style list markers into bullet items.
 * Supports multi-line rows and single-line inline lists.
 */
export function parseBulletItems(text) {
  if (text == null) return null;
  const raw = normalizeBulletSource(text).trim();
  if (!raw || raw === "--") return null;

  const lines = raw.split(/\n/).map((l) => l.trim()).filter(Boolean);

  return parseDashItems(raw, lines) || parseNumberedItems(raw, lines);
}

export function segmentIsListItem(seg) {
  return !!seg && (seg.type === "dash" || seg.type === "numbered");
}

/**
 * Parse text into ordered plain, dash (-), and numbered (1.) segments.
 */
export function parseMixedContent(text) {
  if (text == null) return null;
  const raw = normalizeBulletSource(text);
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "--") return null;

  const nonEmptyLines = raw.split(/\n/).map((l) => l.trim()).filter(Boolean);

  if (nonEmptyLines.length === 1) {
    const singleLine = nonEmptyLines[0];
    const numbered = parseNumberedItems(singleLine, [singleLine]);
    if (numbered && numbered.length > 1) {
      const parts = singleLine.split(/(?:^|(?<=\S))\s*(?=\d+\.\s+)/);
      return parts
        .map((part) => {
          const p = part.trim();
          if (!p) return null;
          const numberedSeg = parseNumberedLine(p);
          if (numberedSeg) return numberedSeg;
          return { type: "plain", text: p };
        })
        .filter(Boolean);
    }

    if (/^-\s/.test(singleLine) && singleLine.includes(" - ")) {
      const parts = singleLine.split(/\s+-\s+/);
      return parts
        .map((part, i) => {
          let s = part.trim();
          if (!s) return null;
          if (i === 0 && !/^-\s/.test(singleLine)) {
            s = s.replace(/^-\s+/, "");
            return { type: "plain", text: s };
          }
          s = s.replace(/^-\s+/, "");
          return { type: "dash", text: s };
        })
        .filter(Boolean);
    }
  }

  const segments = [];
  for (const line of raw.split(/\n/)) {
    const t = line.trim();
    if (!t) {
      segments.push({ type: "plain", text: "" });
      continue;
    }
    const listSeg = parseListLine(t);
    if (listSeg) {
      segments.push(listSeg);
    } else {
      segments.push({ type: "plain", text: t });
    }
  }
  return segments.length ? segments : null;
}

export function hasBulletItems(text) {
  const segments = parseMixedContent(text);
  if (segments) return segments.some(segmentIsListItem);
  return !!parseBulletItems(text);
}
